import React, { useState } from "react";
import { t } from "ttag";

import { IBasket, IBasketLine } from "../../../models/catalogue.interfaces";
import { LinePriceType, getBasketLinePrice } from "../../../utils/catalogue";
import format from "../../../utils/format";
import { groupBy, localeSortBy } from "../../../utils/functional";
import {
    IDineroConvertable,
    getDinero,
    invert,
    sum,
} from "../../../utils/money";

import styles from "./common.module.scss";

const DiscountRow = (props: { name: string; amount: IDineroConvertable }) => (
    <div className={styles.savingsDetailItem}>
        <span>{props.name}</span>
        <div className={styles.priceDiscount}>
            {format.money(invert(props.amount))}
        </div>
    </div>
);

export const BasketLineOfferDetails = (props: {
    line: IBasketLine;
    basket: IBasket;
}): React.ReactNode => {
    const [isSavingsInfoExpanded, setIsSavingsInfoExpanded] = useState(false);

    if (!props.line.price_excl_tax_excl_discounts) {
        return null;
    }

    const onAccordionClick = (event: React.MouseEvent<HTMLElement>): void => {
        if (event) {
            event.preventDefault();
            event.currentTarget.classList.toggle(styles.buttonExpanded);
        }
        setIsSavingsInfoExpanded((isExpanded) => {
            return !isExpanded;
        });
    };

    // Figure out the line totals both before and after discounting.
    const totalBeforeDiscount = getBasketLinePrice(props.basket, props.line, {
        priceType: LinePriceType.RETAIL,
    });
    const totalAfterDiscount = getBasketLinePrice(props.basket, props.line, {
        priceType: LinePriceType.EXCL_TAX_INCL_DISCOUNTS,
        // WE have to subtract the post-discount addons from the total line price
        // in order to display the total discount correctly (since
        // line_price_excl_tax_incl_discounts includes the add-on prices, but
        // the discount total should exclude the add-ons).
        includePostDiscountAddons: false,
    });

    // If the line hasn't been discounted at all, then don't show anything.
    const totalDiscount = totalBeforeDiscount.subtract(totalAfterDiscount);
    if (totalDiscount.isZero() || totalDiscount.isNegative()) {
        return null;
    }

    // Aggregate all the discounts from the primary line, as well as all the
    // lines which are children of it.
    const lines = [props.line].concat(
        props.basket.lines.filter((_line) => _line.parent === props.line.id),
    );
    const rawDiscounts = lines
        .flatMap((line) => line.discounts)
        .map((discount) => ({
            name: discount.voucher_code || discount.offer_name,
            amount: discount.amount,
        }));

    const discounts = [
        ...groupBy(localeSortBy(rawDiscounts, "name"), "name"),
    ].map((group) => ({
        name: group[0].name,
        amount: sum(group.map((d) => getDinero(d.amount))),
    }));

    // Figure out how much of the observed discount (the actual different
    // between the totals we calculated above) is accounted for by the itemized
    // discount details specified in the line. If there's any discrepancy
    // between those two amounts, show the difference as an catch-all "Discount"
    // detail.
    const totalItemizedDiscount = sum(
        discounts.map((discount) => getDinero(discount.amount)),
    );
    const unitemizedDiscount = totalDiscount.subtract(totalItemizedDiscount);

    const savingsDetailID = `basket-line-savings-details-${props.line.product.id}`;
    return (
        <div className={styles.basketLineSavings}>
            <div className={styles.savingsSummary}>
                <button
                    className={styles.button}
                    aria-controls={savingsDetailID}
                    onClick={onAccordionClick}
                    aria-expanded={isSavingsInfoExpanded}
                >
                    {t`Total Savings`}
                </button>
                <div className={styles.priceDiscount}>
                    {format.money(invert(totalDiscount))}
                </div>
            </div>
            {isSavingsInfoExpanded && (
                <div id={savingsDetailID} className={styles.savingsDetails}>
                    {/* Show any discount amount that wasn't itemized */}
                    {unitemizedDiscount.isPositive() &&
                        !unitemizedDiscount.isZero() && (
                            <DiscountRow
                                name={t`Discount`}
                                amount={unitemizedDiscount}
                            />
                        )}
                    {/* Show normal discount details */}
                    {discounts.map((discount, i) => (
                        <DiscountRow
                            key={i}
                            name={discount.name}
                            amount={discount.amount}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
